import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { imantar, limitesDe, meterEnZona } from './encaje.js';
import { disponerEnArco } from './arco.js';
import { cargarFuentes } from './cargar-fuentes.js';
import { acercarEn, ajustar, desplazar, distancia, puntoMedio, VISTA_INICIAL, type Vista } from './vista.js';
import { empezarPinza, esGestoIntencionado, imantarGiro, seguirPinza, type EstadoPinza } from './gestos.js';
import * as fabric from 'fabric';
import {
  layerBBoxMm,
  necesitaUmbral,
  previewRgb,
  type AjustesImagen,
  type CanonicalDocument,
  type Layer,
  type PointMm,
} from '@besbello/design-core';
import { createAdapter, type MmPxAdapter } from './mm-px.js';
import { useEditor } from './document-store.js';

/**
 * LIENZO DEL EDITOR (Fabric.js v6).
 *
 * Regla dura: **el documento manda**. Fabric es una vista. El flujo es:
 *   documento -> objetos Fabric  (sincronizacion)
 *   gesto del usuario -> mm -> `updateLayer` -> documento -> vista
 *
 * Nunca se lee el estado de Fabric como fuente de verdad. Si se hiciera, el
 * redondeo de pixeles del navegador acabaria dentro del documento canonico y de
 * ahi al arte final.
 *
 * D-1 (spike de Safari iOS): el lienzo se limita a 10 capas y usa imagenes
 * PROXY de baja resolucion; el arte de produccion se compone en el servidor y
 * jamas sale de aqui.
 */

export interface FabricCanvasProps {
  document: CanonicalDocument;
  zonePolygonMm: PointMm[];
  /** URL firmada de cada asset, por checksum. */
  assetUrls: Record<string, string>;
  /** Color aproximado de la prenda, para pintar el fondo real. */
  garmentRgb: { r: number; g: number; b: number };
  selectedLayerId: string | null;
  /** Capas con algun diagnostico de error, para resaltarlas. */
  layersWithErrors: string[];
  readOnly?: boolean;
  /** Zoom y encuadre. La pagina los controla para poder poner botones. */
  vista?: Vista;
  onVista?: (vista: Vista) => void;
  /**
   * Sin fondo de prenda: el lienzo se pone ENCIMA de la foto del producto.
   *
   * Pintar aqui el color de la prenda taparia la fotografia, que es justo lo
   * que el cliente ha venido a ver.
   */
  transparente?: boolean;
  /** Como se llama el lienzo para un lector de pantalla. */
  etiqueta?: string;
  /**
   * Recibe una funcion para sacar SOLO EL DISENO como imagen.
   *
   * La usa la vista en tres dimensiones, que necesita el arte sin el color de
   * la prenda ni el recuadro de la zona: eso ya lo pone la propia prenda del
   * modelo. Se expone asi, y no exportando el lienzo entero desde fuera, porque
   * apagar el fondo y esconder la zona son detalles de COMO se pinta aqui.
   */
  onExportador?: (exportar: () => string | null) => void;
}

const ZONE_STROKE = '#b81d2c';
const ERROR_STROKE = '#b3261e';

export function FabricCanvas(props: FabricCanvasProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const fabricRef = useRef<fabric.Canvas | null>(null);
  const adapterRef = useRef<MmPxAdapter | null>(null);
  /** Evita el bucle "Fabric mueve -> actualizo documento -> redibujo -> Fabric mueve". */
  const syncingRef = useRef(false);
  /** Alt pulsada: se salta el imantado y los limites de la zona. */
  const saltarLimitesRef = useRef(false);
  /*
    El manejador de Fabric se registra UNA vez y viviria con las props del
    primer pintado. Estos espejos le dan siempre las actuales: sin ellos, mover
    una capa despues de cambiar de talla usaria la zona vieja.
  */
  const documentoRef = useRef(props.document);
  documentoRef.current = props.document;
  const zonaRef = useRef(props.zonePolygonMm);
  /** Guias del iman en vivo, pintadas en contextTop en after:render. */
  const guiasRef = useRef<import('./encaje.js').Guia[]>([]);
  zonaRef.current = props.zonePolygonMm;
  // Espejo de la capa seleccionada, legible desde los manejadores de Fabric sin
  // volver a suscribir el lienzo en cada cambio de seleccion.
  const selectedRef = useRef(props.selectedLayerId);
  selectedRef.current = props.selectedLayerId;

  // Claves de CONTENIDO de las props que llegan como objetos literales. Ver la
  // nota en las dependencias del efecto de sincronizacion.
  const garmentKey = `${props.garmentRgb.r},${props.garmentRgb.g},${props.garmentRgb.b}`;
  const errorsKey = props.layersWithErrors.join(',');
  const zoneKey = props.zonePolygonMm.map((p) => `${p.x},${p.y}`).join(' ');
  const [size, setSize] = useState({ width: 0, height: 0 });
  /*
    Tipografias en uso. Cambia de valor cuando terminan de descargarse, y eso
    fuerza un repintado: el ancho de la caja y las posiciones del arco se MIDEN,
    y medirlos con la fuente de sustitucion deja el texto cortado para siempre.
  */
  const fuentesEnUso = props.document.layers
    .filter((l): l is Extract<Layer, { kind: 'text' }> => l.kind === 'text')
    .map((l) => ({ family: l.font.family, weight: l.font.weight, italic: l.font.italic }));
  const fuentesKey = fuentesEnUso.map((f) => `${f.family}|${f.weight}|${f.italic}`).join(',');
  const [fuentesCargadas, setFuentesCargadas] = useState('');
  /*
    La vista vive fuera cuando la pagina la controla, y aqui si no. Asi el
    lienzo funciona solo —en el mockup, por ejemplo— sin obligar a quien lo use
    a llevarle la cuenta del zoom.
  */
  const [vistaInterna, setVistaInterna] = useState<Vista>(VISTA_INICIAL);
  const vista = props.vista ?? vistaInterna;
  const cambiarVista = props.onVista ?? setVistaInterna;
  const vistaRef = useRef(vista);
  vistaRef.current = vista;
  const cambiarVistaRef = useRef(cambiarVista);
  cambiarVistaRef.current = cambiarVista;
  /** Arrastre de la vista: con la barra espaciadora o con el boton central. */
  const arrastrandoRef = useRef<{ x: number; y: number } | null>(null);
  const espacioRef = useRef(false);
  /** Separacion entre los dos dedos al empezar la pinza (zoom de la vista). */
  const pinzaRef = useRef<{ distancia: number; zoom: number } | null>(null);
  /*
    Pinza sobre una CAPA: escalar y girar con dos dedos.

    Es distinta de la pinza de la vista: con una capa seleccionada, dos dedos
    manipulan esa capa; sin nada seleccionado, mueven el encuadre. Es lo que
    espera cualquiera que haya usado el movil, y sin ello en el movil solo se
    podia arrastrar.
  */
  const gestoCapaRef = useRef<{ inicio: EstadoPinza; capaId: string; transformInicial: Layer['transform'] } | null>(
    null,
  );
  /** Posicion del grupo al empezar a moverlo, para saber cuanto se desplazo. */
  const grupoRef = useRef<{ left: number; top: number } | null>(null);

  useEffect(() => {
    if (fuentesEnUso.length === 0) return;
    let vivo = true;
    void cargarFuentes(fuentesEnUso).then(() => {
      if (vivo) setFuentesCargadas(fuentesKey);
    });
    return () => {
      vivo = false;
    };
    // `fuentesKey` resume el contenido: `fuentesEnUso` es un array nuevo en cada
    // render, y depender de su identidad relanzaria la carga sin parar.
  }, [fuentesKey]);

  const updateLayer = useEditor((s) => s.updateLayer);
  const select = useEditor((s) => s.select);
  const seleccionarVarias = useEditor((s) => s.seleccionarVarias);
  const desplazarSeleccion = useEditor((s) => s.desplazarSeleccion);
  const seleccionadas = useEditor((s) => s.seleccionadas);
  const seleccionadasRef = useRef(seleccionadas);
  seleccionadasRef.current = seleccionadas;
  const seleccionadasKey = seleccionadas.join(',');

  // --- tamano del lienzo: se recalcula al girar el movil o cambiar de panel ---
  useLayoutEffect(() => {
    const element = containerRef.current;
    if (!element) return;
    const observer = new ResizeObserver(() => {
      const rect = element.getBoundingClientRect();
      setSize({ width: Math.floor(rect.width), height: Math.floor(rect.height) });
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  // --- creacion del lienzo ---------------------------------------------------
  useEffect(() => {
    if (!canvasRef.current || size.width === 0 || size.height === 0) return;

    const canvas = new fabric.Canvas(canvasRef.current, {
      width: size.width,
      height: size.height,
      backgroundColor: 'transparent',
      selection: !props.readOnly,
      preserveObjectStacking: true,
      // En movil, el manejador de rotacion tapa la capa con el pulgar.
      controlsAboveOverlay: true,
    });

    fabricRef.current = canvas;

    // Alt suspende el imantado; espacio pasa a arrastrar la vista.
    const teclaAbajo = (e: KeyboardEvent) => {
      if (e.altKey) saltarLimitesRef.current = true;
      if (e.code === 'Space' && !esCampoEditable(e.target)) {
        espacioRef.current = true;
        // Sin esto la pagina baja una pantalla en cuanto se pulsa espacio, que
        // es exactamente lo contrario de lo que se pretende.
        e.preventDefault();
      }
    };
    const teclaArriba = (e: KeyboardEvent) => {
      if (!e.altKey) saltarLimitesRef.current = false;
      if (e.code === 'Space') {
        espacioRef.current = false;
        arrastrandoRef.current = null;
      }
    };
    window.addEventListener('keydown', teclaAbajo);
    window.addEventListener('keyup', teclaArriba);

    canvas.on('object:modified', (event) => {
      if (syncingRef.current) return;
      const target = event.target;
      const adapter = adapterRef.current;
      if (!target || !adapter) return;

      const layerId = (target as fabric.Object & { layerId?: string }).layerId;
      if (!layerId) return;

      // De pixeles de pantalla a MILIMETROS del documento. Esta es la unica
      // direccion en la que la vista puede escribir en el documento.
      const mm = adapter.pointToMm({ x: target.left ?? 0, y: target.top ?? 0 });
      const scaleX = (target.scaleX ?? 1) * (target.width ?? 1);
      const scaleY = (target.scaleY ?? 1) * (target.height ?? 1);
      const angle = ((target.angle ?? 0) * Math.PI) / 180;
      const cos = Math.cos(angle);
      const sin = Math.sin(angle);

      const widthMm = adapter.toMm(scaleX);
      const heightMm = adapter.toMm(scaleY);

      /*
        LIMITES E IMANTADO, en milimetros y antes de escribir en el documento.

        Se hace aqui y no al pintar porque el documento es la verdad: si la capa
        se guardara fuera de la zona y solo se corrigiera en pantalla, el taller
        recibiria una cosa y el cliente habria visto otra.

        Con `Alt` pulsada se salta: hay casos legitimos —un sangrado a propósito—
        y quien lo hace sabe lo que hace. El aviso de «se sale» sigue ahi.
      */
      const zona = limitesDe(zonaRef.current);
      const otras = documentoRef.current.layers
        .filter((l) => l.id !== layerId)
        .map((l) => {
          const b = layerBBoxMm(l);
          return { x: b.minX, y: b.minY, anchoMm: b.maxX - b.minX, altoMm: b.maxY - b.minY };
        });

      let caja = { x: mm.x, y: mm.y, anchoMm: widthMm, altoMm: heightMm };
      if (!saltarLimitesRef.current) {
        caja = imantar(caja, zona, otras).caja;
        caja = meterEnZona(caja, zona);
      }

      updateLayer(layerId, {
        transform: {
          a: cos * widthMm,
          b: sin * widthMm,
          c: -sin * heightMm,
          d: cos * heightMm,
          e: caja.x,
          f: caja.y,
        },
      } as Partial<Layer>);
    });

    const onSelection = () => {
      if (syncingRef.current) return;
      /*
        Fabric admite seleccionar varias con Shift o con un marco arrastrado, y
        entonces el objeto activo es un `ActiveSelection` que agrupa a las
        demas. Se leen todas: con una sola, esto se comporta igual que antes.
      */
      const activos = canvas.getActiveObjects() as Array<fabric.Object & { layerId?: string }>;
      const ids = activos.map((o) => o.layerId).filter((id): id is string => Boolean(id));

      // Segunda barrera: avisar de una seleccion que YA es la vigente solo sirve
      // para provocar otro repintado. La guarda de arriba deberia bastar; esta
      // esta porque el precio de equivocarse era la pestana entera.
      if (ids.join(',') === seleccionadasRef.current.join(',')) return;
      if (ids.length <= 1) select(ids[0] ?? null);
      else seleccionarVarias(ids);
    };
    canvas.on('selection:created', onSelection);
    canvas.on('selection:updated', onSelection);
    canvas.on('selection:cleared', () => {
      if (!syncingRef.current) select(null);
    });

    /*
      MOVER VARIAS A LA VEZ.

      Fabric mueve el grupo y deja a cada hijo con coordenadas RELATIVAS al
      grupo, asi que `object:modified` no sirve: daria posiciones que no son las
      del documento. En su lugar se toma el desplazamiento del grupo entero y se
      aplica igual a todas, que es lo que el usuario ha hecho de verdad.
    */
    canvas.on('object:moving', (event) => {
      const target = event.target as fabric.Object & { layerId?: string };
      if (!target) return;
      if (!target.layerId) {
        // Seleccion multiple: el desplazamiento del grupo lo lleva object:modified.
        grupoRef.current = { left: target.left ?? 0, top: target.top ?? 0 };
        return;
      }

      /*
        IMAN EN VIVO, no solo al soltar.

        `imantar` ya corregia la caja en `object:modified`, pero eso es DESPUES
        de soltar: el cliente no ve el iman actuar ni sabe por que la capa dio
        un saltito al final. Aqui se aplica el MISMO calculo durante el
        arrastre —la unica fuente de candidatos es esa funcion, no hay una
        segunda logica de iman— y se apuntan las guias para pintarlas.

        Con Alt se salta, igual que el imantado de siempre: mismo gesto, mismo
        significado. Y el documento NO se toca aqui: esto solo mueve la vista;
        la escritura en mm sigue pasando una unica vez, al soltar.
      */
      const adapter = adapterRef.current;
      if (!adapter || saltarLimitesRef.current) {
        guiasRef.current = [];
        return;
      }
      const mm = adapter.pointToMm({ x: target.left ?? 0, y: target.top ?? 0 });
      const anchoMm = adapter.toMm((target.scaleX ?? 1) * (target.width ?? 1));
      const altoMm = adapter.toMm((target.scaleY ?? 1) * (target.height ?? 1));
      const zona = limitesDe(zonaRef.current);
      const otras = documentoRef.current.layers
        .filter((l) => l.id !== target.layerId)
        .map((l) => {
          const b = layerBBoxMm(l);
          return { x: b.minX, y: b.minY, anchoMm: b.maxX - b.minX, altoMm: b.maxY - b.minY };
        });

      const r = imantar({ x: mm.x, y: mm.y, anchoMm, altoMm }, zona, otras);
      if (r.caja.x !== mm.x) target.set('left', adapter.toPx(r.caja.x) + adapter.offsetXPx);
      if (r.caja.y !== mm.y) target.set('top', adapter.toPx(r.caja.y) + adapter.offsetYPx);
      guiasRef.current = r.guias;
    });

    /*
      LAS GUIAS SE PINTAN EN EL LIENZO SUPERIOR de Fabric (`contextTop`), que no
      pasa por la sincronizacion documento<->canvas. Un objeto Fabric temporal
      se colaria en la serializacion; una linea dibujada aqui desaparece sola en
      el siguiente render.
    */
    canvas.on('after:render', () => {
      const guias = guiasRef.current;
      const adapter = adapterRef.current;
      if (guias.length === 0 || !adapter) return;
      const ctx = canvas.contextTop;
      if (!ctx) return;
      ctx.save();
      ctx.strokeStyle = '#e11d48';
      ctx.setLineDash([6, 4]);
      ctx.lineWidth = 1;
      for (const guia of guias) {
        ctx.beginPath();
        if (guia.eje === 'x') {
          const x = adapter.toPx(guia.mm) + adapter.offsetXPx;
          ctx.moveTo(x, 0);
          ctx.lineTo(x, canvas.getHeight());
        } else {
          const y = adapter.toPx(guia.mm) + adapter.offsetYPx;
          ctx.moveTo(0, y);
          ctx.lineTo(canvas.getWidth(), y);
        }
        ctx.stroke();
      }
      ctx.restore();
    });

    canvas.on('mouse:up', () => {
      if (guiasRef.current.length === 0) return;
      guiasRef.current = [];
      canvas.requestRenderAll();
    });

    canvas.on('object:modified', (event) => {
      if (syncingRef.current) return;
      const target = event.target as fabric.Object & { layerId?: string };
      const adapter = adapterRef.current;
      const inicio = grupoRef.current;
      grupoRef.current = null;
      if (!target || target.layerId || !adapter || !inicio) return;

      const dx = adapter.toMm((target.left ?? 0) - inicio.left);
      const dy = adapter.toMm((target.top ?? 0) - inicio.top);
      if (dx !== 0 || dy !== 0) desplazarSeleccion(dx, dy);
    });

    /*
      ZOOM CON LA RUEDA, sobre el punto donde esta el cursor.

      Solo con Ctrl (o Cmd) pulsado: la rueda a secas tiene que seguir
      desplazando la pagina, o el editor se convierte en una trampa de la que no
      se puede salir con la rueda.
    */
    const alRodar = (evento: WheelEvent) => {
      if (!evento.ctrlKey && !evento.metaKey) return;
      evento.preventDefault();
      const caja = canvas.getElement().getBoundingClientRect();
      const factor = evento.deltaY < 0 ? 1.12 : 1 / 1.12;
      cambiarVistaRef.current(
        acercarEn(vistaRef.current, factor, evento.clientX - caja.left, evento.clientY - caja.top),
      );
    };
    const elemento = canvas.upperCanvasEl;
    elemento.addEventListener('wheel', alRodar, { passive: false });

    // Doble pulsacion en el vacio: vuelve a ver la zona entera.
    const alDoblePulsar = (evento: MouseEvent) => {
      if (canvas.findTarget(evento)) return;
      cambiarVistaRef.current(ajustar());
    };
    elemento.addEventListener('dblclick', alDoblePulsar);

    /*
      ARRASTRE DE LA VISTA y PINZA.

      Con la barra espaciadora pulsada, arrastrar mueve el encuadre en vez de la
      capa; en el movil, dos dedos hacen lo mismo y ademas acercan. El lienzo ya
      tiene `touch-action: none`, asi que estos gestos no se los lleva el
      navegador.
    */
    const alEmpezar = (evento: PointerEvent | TouchEvent) => {
      const toques = 'touches' in evento ? evento.touches : null;
      if (toques && toques.length === 2) {
        const [a, b] = [toques[0]!, toques[1]!];

        /*
          Con una capa elegida, los dos dedos son PARA ELLA.

          Sin nada elegido, mueven el encuadre. Distinguirlo por la selección y
          no por dónde caen los dedos evita el caso desagradable: empezar a
          escalar una capa por tocar sin querer encima de ella.
        */
        const capaId = seleccionadasRef.current[0];
        const capa = capaId ? documentoRef.current.layers.find((l) => l.id === capaId) : undefined;
        if (capa && !capa.locked) {
          gestoCapaRef.current = {
            inicio: empezarPinza(a, b),
            capaId: capa.id,
            transformInicial: capa.transform,
          };
          return;
        }

        pinzaRef.current = { distancia: distancia(a, b), zoom: vistaRef.current.zoom };
        canvas.discardActiveObject();
        canvas.renderAll();
        return;
      }
      if (!toques && espacioRef.current) {
        const e = evento as PointerEvent;
        arrastrandoRef.current = { x: e.clientX, y: e.clientY };
      }
    };

    const alMover = (evento: PointerEvent | TouchEvent) => {
      const toques = 'touches' in evento ? evento.touches : null;

      const gesto = gestoCapaRef.current;
      if (toques && toques.length === 2 && gesto) {
        evento.preventDefault();
        const cambio = seguirPinza(gesto.inicio, toques[0]!, toques[1]!);
        // Al posar dos dedos siempre hay temblor: sin umbral, una capa se
        // escala y se gira cada vez que alguien la toca con dos dedos.
        if (!esGestoIntencionado(cambio)) return;

        const t = gesto.transformInicial;
        const adapter = adapterRef.current;
        const anchoMm = Math.hypot(t.a, t.b) * cambio.escala;
        const altoMm = Math.hypot(t.c, t.d) * cambio.escala;
        const giroActual = (Math.atan2(t.b, t.a) * 180) / Math.PI;
        // Nadie quiere un logotipo a 44,7 grados: cerca del recto, al recto.
        const grados = imantarGiro(giroActual + cambio.giroGrados);
        const radianes = (grados * Math.PI) / 180;

        updateLayer(gesto.capaId, {
          transform: {
            a: Math.cos(radianes) * anchoMm,
            b: Math.sin(radianes) * anchoMm,
            c: -Math.sin(radianes) * altoMm,
            d: Math.cos(radianes) * altoMm,
            e: t.e + (adapter ? adapter.toMm(cambio.dx) : 0),
            f: t.f + (adapter ? adapter.toMm(cambio.dy) : 0),
          },
        } as Partial<Layer>);
        return;
      }

      if (toques && toques.length === 2 && pinzaRef.current) {
        evento.preventDefault();
        const [a, b] = [toques[0]!, toques[1]!];
        const caja = canvas.getElement().getBoundingClientRect();
        const medio = puntoMedio(a, b);
        const factor = distancia(a, b) / pinzaRef.current.distancia;
        const objetivo = pinzaRef.current.zoom * factor;
        cambiarVistaRef.current(
          acercarEn(
            { ...vistaRef.current, zoom: vistaRef.current.zoom },
            objetivo / vistaRef.current.zoom,
            medio.x - caja.left,
            medio.y - caja.top,
          ),
        );
        return;
      }
      const arrastre = arrastrandoRef.current;
      if (arrastre && !toques) {
        const e = evento as PointerEvent;
        cambiarVistaRef.current(desplazar(vistaRef.current, e.clientX - arrastre.x, e.clientY - arrastre.y));
        arrastrandoRef.current = { x: e.clientX, y: e.clientY };
      }
    };

    const alTerminar = () => {
      arrastrandoRef.current = null;
      pinzaRef.current = null;
      gestoCapaRef.current = null;
    };

    elemento.addEventListener('pointerdown', alEmpezar);
    elemento.addEventListener('pointermove', alMover);
    elemento.addEventListener('pointerup', alTerminar);
    elemento.addEventListener('touchstart', alEmpezar, { passive: false });
    elemento.addEventListener('touchmove', alMover, { passive: false });
    elemento.addEventListener('touchend', alTerminar);

    return () => {
      window.removeEventListener('keydown', teclaAbajo);
      window.removeEventListener('keyup', teclaArriba);
      elemento.removeEventListener('wheel', alRodar);
      elemento.removeEventListener('dblclick', alDoblePulsar);
      elemento.removeEventListener('pointerdown', alEmpezar);
      elemento.removeEventListener('pointermove', alMover);
      elemento.removeEventListener('pointerup', alTerminar);
      elemento.removeEventListener('touchstart', alEmpezar);
      elemento.removeEventListener('touchmove', alMover);
      elemento.removeEventListener('touchend', alTerminar);
      void canvas.dispose();
      fabricRef.current = null;
    };
  }, [size.width, size.height, props.readOnly, select, updateLayer, seleccionarVarias, desplazarSeleccion]);

  /*
    APLICAR EL ZOOM Y EL ENCUADRE.

    Va en su propio efecto para que mover la vista NO reconstruya las capas: el
    lienzo entero se rehace en el efecto de sincronizacion, y hacerlo en cada
    cuadro de un arrastre seria inaceptable.
  */
  useEffect(() => {
    const canvas = fabricRef.current;
    if (!canvas) return;
    canvas.setViewportTransform([vista.zoom, 0, 0, vista.zoom, vista.x, vista.y]);
    canvas.renderAll();
  }, [vista.zoom, vista.x, vista.y]);

  // --- sincronizacion documento -> vista -------------------------------------
  useEffect(() => {
    const canvas = fabricRef.current;
    if (!canvas || size.width === 0) return;

    const zoneBounds = boundsOf(props.zonePolygonMm);
    const adapter = createAdapter(
      { widthMm: props.document.canvasMm.width, heightMm: props.document.canvasMm.height },
      { widthPx: size.width, heightPx: size.height },
    );
    adapterRef.current = adapter;

    syncingRef.current = true;
    canvas.remove(...canvas.getObjects());

    // 1. Prenda: el color REAL del producto, para que el contraste que ve el
    //    cliente sea el que tendra la prenda.
    const { r, g, b } = props.garmentRgb;
    canvas.backgroundColor = props.transparente ? 'transparent' : `rgb(${r},${g},${b})`;

    // 2. Zona de marcaje: el limite fisico de lo imprimible.
    const zonePoints = props.zonePolygonMm.map((p) =>
      adapter.pointToPx({ x: p.x - zoneBounds.minX, y: p.y - zoneBounds.minY }),
    );
    const zone = new fabric.Polygon(zonePoints, {
      fill: 'transparent',
      // Sobre la foto, el limite ya lo marca el recorte del contenedor: otra
      // linea de puntos a plena intensidad solo ensucia la vista previa.
      stroke: props.transparente ? 'rgba(184,29,44,0.35)' : ZONE_STROKE,
      strokeWidth: 1.5,
      strokeDashArray: [6, 4],
      selectable: false,
      evented: false,
      objectCaching: false,
    });
    canvas.add(zone);

    // 3. Capas, en orden de `z`.
    const ordered = [...props.document.layers].sort((a, b) => a.z - b.z);
    const pending: Promise<void>[] = [];

    for (const layer of ordered) {
      const object = buildObject(layer, adapter, props.assetUrls, props.layersWithErrors.includes(layer.id));
      if (object instanceof Promise) {
        pending.push(
          object.then((resolved) => {
            if (resolved) canvas.add(resolved);
          }),
        );
      } else if (object) {
        canvas.add(object);
      }
    }

    canvas.renderAll();

    /*
      La bandera se levanta cuando TERMINA de reconstruirse el lienzo, no antes.

      Estaba tambien en la linea siguiente al `Promise.all`, es decir, se bajaba
      de forma SINCRONA mientras la reconstruccion seguia pendiente. Cuando
      llegaba el `.then`, `setActiveObject` emitia `selection:created` sin
      guarda, eso llamaba a `select()`, eso repintaba, y el repintado volvia a
      llamar a `setActiveObject`: un bucle que creaba objetos de Fabric sin
      parar hasta reventar la pestana del navegador. Se reproducia con solo
      pulsar "Texto", porque anadir una capa la deja seleccionada.
    */
    void Promise.all(pending).then(() => {
      // Reponer la seleccion tras redibujar: perderla en cada cambio haria que
      // el panel de propiedades parpadease.
      const objetos = canvas
        .getObjects()
        .filter((o) => seleccionadasRef.current.includes((o as fabric.Object & { layerId?: string }).layerId ?? ''));

      if (objetos.length > 1) {
        // Varias: se reagrupan en una seleccion activa para poder moverlas juntas.
        canvas.setActiveObject(new fabric.ActiveSelection(objetos, { canvas }));
      } else if (objetos.length === 1) {
        canvas.setActiveObject(objetos[0]!);
      } else {
        canvas.discardActiveObject();
      }
      canvas.renderAll();
      syncingRef.current = false;
    });
    /*
      Las dependencias son CLAVES DE CONTENIDO, no identidades de objeto.

      La pagina del editor pasa `garmentRgb={{...}}` y un array de capas con
      error recien calculado: objetos nuevos en cada render. Dependiendo de la
      identidad, el lienzo entero se destruia y se reconstruia en cada pintado
      —decenas de veces por segundo mientras se arrastra— y ademas alimentaba
      el bucle de seleccion. Comparando el contenido, se reconstruye cuando algo
      cambia de verdad.
    */
  }, [
    props.document,
    zoneKey,
    props.assetUrls,
    garmentKey,
    props.transparente,
    seleccionadasKey,
    errorsKey,
    size.width,
    size.height,
    // Repinta cuando las tipografias YA estan cargadas, para volver a medir el
    // ancho del texto con la buena y no con la de sustitucion.
    fuentesCargadas,
  ]);

  /*
    Exportar SOLO EL DISENO.

    Se apaga el fondo y se esconde el recuadro de la zona, se dibuja, se saca la
    imagen y se deja todo como estaba —dentro del mismo cuadro, asi que en
    pantalla no parpadea nada—. Es la unica forma de obtener el arte sobre
    transparente sin montar un segundo lienzo con el mismo contenido, que seria
    dos sitios donde arreglar el mismo fallo.
  */
  useEffect(() => {
    if (!props.onExportador) return;
    props.onExportador(() => {
      const canvas = fabricRef.current;
      if (!canvas) {
        /*
          Sin lienzo no hay nada que exportar, y conviene DECIRLO.

          Aquí se llegó una vez porque el lienzo se ocultaba con `display: none`
          y Fabric se destruye solo al medir cero. El síntoma era «el 3D no
          enseña el diseño» y no había ni un mensaje en ninguna parte.
        */
        console.warn('[editor] se pidió exportar el diseño sin lienzo montado');
        return null;
      }

      const fondoPrevio = canvas.backgroundColor;
      const zona = canvas.getObjects().find((o) => o instanceof fabric.Polygon);
      const zonaVisible = zona?.visible ?? true;

      try {
        canvas.backgroundColor = 'transparent';
        if (zona) zona.visible = false;
        canvas.renderAll();
        return canvas.toDataURL({ format: 'png', multiplier: 1 });
      } catch {
        // Un lienzo «manchado» por una imagen de otro origen no se puede
        // exportar. El 3D se queda sin estampado y el editor sigue igual.
        return null;
      } finally {
        canvas.backgroundColor = fondoPrevio;
        if (zona) zona.visible = zonaVisible;
        canvas.renderAll();
      }
    });
  }, [props.onExportador]);

  return (
    /*
      El lienzo RECIBE EL FOCO.

      Un `<canvas>` no lo recibe por su cuenta, así que quien navega con teclado
      no podía llegar hasta aquí: llegaba al panel de propiedades y las flechas
      —que sí funcionaban— nunca le tocaban. Con `tabIndex` entra en el orden de
      tabulación como cualquier control.

      La etiqueta y el papel de aplicación le dicen al lector de pantalla que
      aquí las flechas las gestiona la página y no debe robárselas para navegar
      por el documento, que es lo que hace por defecto.
    */
    <div
      ref={containerRef}
      className="lienzo-tactil relative h-full w-full focus-visible:outline focus-visible:outline-2 focus-visible:outline-marca"
      tabIndex={0}
      role="application"
      aria-label={props.etiqueta ?? 'Lienzo del diseño'}
    >
      <canvas ref={canvasRef} className="absolute inset-0" />
    </div>
  );
}

/**
 * Si el foco esta en un campo donde escribir.
 *
 * Sin esto, la barra espaciadora dejaria de escribir espacios en el nombre de
 * la capa y pasaria a arrastrar el lienzo, que es de las cosas que mas
 * desconcierta de un editor.
 */
function esCampoEditable(destino: EventTarget | null): boolean {
  if (!(destino instanceof HTMLElement)) return false;
  const etiqueta = destino.tagName;
  return etiqueta === 'INPUT' || etiqueta === 'TEXTAREA' || etiqueta === 'SELECT' || destino.isContentEditable;
}

/**
 * Los ajustes de imagen, con los filtros de Fabric.
 *
 * El calculo de la matriz vive en `design-core` y lo comparten el lienzo, el
 * mockup y el compositor de produccion: si cada uno interpretara «contraste
 * 1,4» a su manera, el cliente aprobaria una cosa y el taller imprimiria otra.
 */
function aplicarAjustes(imagen: fabric.FabricImage, ajustes: AjustesImagen | undefined): void {
  if (!ajustes) return;
  const filtros: fabric.filters.BaseFilter<string>[] = [];

  if (ajustes.brillo !== 1) {
    // Fabric mide el brillo como un desplazamiento de -1 a 1, no como un
    // multiplicador: 1,2 de los nuestros es +0,2 de los suyos.
    filtros.push(new fabric.filters.Brightness({ brightness: ajustes.brillo - 1 }));
  }
  if (ajustes.contraste !== 1) {
    filtros.push(new fabric.filters.Contrast({ contrast: ajustes.contraste - 1 }));
  }

  if (ajustes.modo === 'grises') {
    filtros.push(new fabric.filters.Grayscale());
  } else if (necesitaUmbral(ajustes)) {
    filtros.push(new fabric.filters.Grayscale());
    // El corte por umbral no es lineal, asi que no lo hace una matriz: Fabric
    // no trae un filtro de umbral, y se consigue con un contraste extremo
    // centrado en el punto de corte.
    filtros.push(new fabric.filters.Brightness({ brightness: 0.5 - ajustes.umbral }));
    filtros.push(new fabric.filters.Contrast({ contrast: 0.98 }));
  } else if (ajustes.saturacion !== 1) {
    filtros.push(new fabric.filters.Saturation({ saturation: ajustes.saturacion - 1 }));
  }

  imagen.filters = filtros;
  imagen.applyFilters();
}

function boundsOf(polygon: PointMm[]): { minX: number; minY: number } {
  return { minX: Math.min(...polygon.map((p) => p.x)), minY: Math.min(...polygon.map((p) => p.y)) };
}

function cssColor(color: { type: string } & Record<string, unknown>): string {
  const { r, g, b } = previewRgb(color as never);
  return `rgb(${r},${g},${b})`;
}

function buildObject(
  layer: Layer,
  adapter: MmPxAdapter,
  assetUrls: Record<string, string>,
  hasError: boolean,
): fabric.Object | Promise<fabric.Object | null> | null {
  const position = adapter.pointToPx({ x: layer.transform.e, y: layer.transform.f });
  const angle = (Math.atan2(layer.transform.b, layer.transform.a) * 180) / Math.PI;
  const scaleXMm = Math.hypot(layer.transform.a, layer.transform.b);
  const scaleYMm = Math.hypot(layer.transform.c, layer.transform.d);

  const common: Partial<fabric.Object> & { layerId: string } = {
    left: position.x,
    top: position.y,
    angle,
    originX: 'left',
    originY: 'top',
    opacity: layer.opacity,
    selectable: !layer.locked,
    evented: !layer.locked,
    hasControls: !layer.locked,
    // Un objetivo tactil pequeno en movil es imposible de agarrar.
    cornerSize: 18,
    touchCornerSize: 28,
    transparentCorners: false,
    cornerColor: '#111111',
    borderColor: hasError ? ERROR_STROKE : '#111111',
    borderScaleFactor: hasError ? 2.5 : 1,
    layerId: layer.id,
  };

  if (layer.kind === 'image') {
    const url = assetUrls[layer.assetChecksum];
    if (!url) {
      // Sin URL (aun cargando o asset caducado) se pinta un hueco visible, no
      // se omite la capa: omitirla haria creer que se perdio.
      return new fabric.Rect({
        ...common,
        width: adapter.toPx(scaleXMm),
        height: adapter.toPx(scaleYMm),
        fill: 'rgba(0,0,0,0.08)',
        stroke: '#999',
        strokeDashArray: [4, 4],
      } as never);
    }
    return fabric.FabricImage.fromURL(url, { crossOrigin: 'anonymous' }).then((image) => {
      /*
        RECORTE de la imagen origen.

        Fabric lo expresa en pixeles del fichero, y el documento en fracciones,
        que es lo que sobrevive a que el editor sirva una version reducida del
        asset. La conversion se hace aqui y en ningun otro sitio.
      */
      const anchoFuente = image.width ?? 1;
      const altoFuente = image.height ?? 1;
      const recorte = layer.recorte;
      const anchoUtil = recorte ? anchoFuente * recorte.widthFrac : anchoFuente;
      const altoUtil = recorte ? altoFuente * recorte.heightFrac : altoFuente;

      image.set({
        ...common,
        ...(recorte
          ? {
              cropX: anchoFuente * recorte.xFrac,
              cropY: altoFuente * recorte.yFrac,
              width: anchoUtil,
              height: altoUtil,
            }
          : {}),
        scaleX: adapter.toPx(scaleXMm) / anchoUtil,
        scaleY: adapter.toPx(scaleYMm) / altoUtil,
      } as never);

      aplicarAjustes(image, layer.ajustes);
      return image;
    });
  }

  if (layer.kind === 'text') {
    const visible = aplicarCaja(layer.text, layer.textCase);
    const estiloTexto = {
      fontSize: adapter.toPx(layer.sizeMm),
      fontFamily: layer.font.family,
      fontWeight: layer.font.weight,
      fontStyle: layer.font.italic ? 'italic' : 'normal',
      charSpacing: layer.tracking * 100,
      fill: cssColor(layer.color),
      // Contorno: se dibuja centrado sobre el borde, asi que en pantalla se ve
      // la mitad del grosor por fuera —igual que saldra impreso.
      stroke: layer.outline ? cssColor(layer.outline.color) : undefined,
      strokeWidth: layer.outline ? adapter.toPx(layer.outline.widthMm) : 0,
      paintFirst: 'stroke' as const,
      strokeLineJoin: 'round' as const,
    };

    if (layer.arc) {
      return construirArco(layer, visible, estiloTexto, adapter, common);
    }

    const caja = new fabric.Textbox(visible, {
      ...common,
      ...estiloTexto,
      // Ancho de partida solo para poder construirlo; se corrige justo debajo.
      width: Math.max(40, adapter.toPx(layer.sizeMm * visible.length * 0.55)),
      lineHeight: layer.lineHeightMm / layer.sizeMm,
      textAlign: layer.align,
      editable: !layer.locked,
      splitByGrapheme: false,
    } as never);

    /*
      El ancho se MIDE con la tipografia ya cargada, no se estima.

      Estaba calculado como `sizeMm * numero de caracteres * 0.55`, y ese factor
      se queda corto en cuanto la tipografia es ancha o el texto va en
      mayusculas: "BESBELLO" se veia como "BESBELLC", con la ultima letra
      cortada por el borde de la caja. En un editor donde el lienzo ES lo que se
      va a imprimir, ver el texto cortado hace que el cliente lo cambie sin que
      haga falta.
    */
    const medido = caja.calcTextWidth();
    if (Number.isFinite(medido) && medido > 0) {
      caja.set({ width: medido + adapter.toPx(layer.sizeMm * 0.2) });
    }
    return caja;
  }

  // Forma: el path viene en MM, asi que se escala a pixeles con la misma regla.
  const path = new fabric.Path(layer.path, {
    ...common,
    fill: layer.fill ? cssColor(layer.fill) : 'transparent',
    stroke: layer.stroke ? cssColor(layer.stroke) : undefined,
    strokeWidth: layer.strokeWidthMm ? adapter.toPx(layer.strokeWidthMm) : 0,
    scaleX: adapter.scale,
    scaleY: adapter.scale,
  } as never);
  return path;
}

/**
 * Mayusculas/minusculas: transformacion SOLO VISUAL.
 *
 * El texto original se conserva intacto en `layer.text`. Si se guardara ya
 * convertido, quien quisiera volver a "Besbello" desde "BESBELLO" tendria que
 * reescribirlo entero, y el pedido perderia como lo escribio el cliente.
 */
function aplicarCaja(texto: string, caja: 'none' | 'upper' | 'lower'): string {
  if (caja === 'upper') return texto.toLocaleUpperCase('es');
  if (caja === 'lower') return texto.toLocaleLowerCase('es');
  return texto;
}

/**
 * Texto en arco: un grupo con una letra por objeto.
 *
 * Fabric no cura texto, asi que se coloca cada caracter con la geometria de
 * `arco.ts` —la MISMA que usara el compositor de produccion— y se agrupan. El
 * grupo se mueve y se gira como cualquier capa; el texto se edita desde el
 * panel, no pinchando encima, porque un grupo de letras sueltas no tiene cursor.
 */
function construirArco(
  layer: Extract<Layer, { kind: 'text' }>,
  visible: string,
  estilo: Record<string, unknown>,
  adapter: MmPxAdapter,
  common: Partial<fabric.Object> & { layerId: string },
): fabric.Object {
  const arco = layer.arc as { radiusMm: number; direction: 'up' | 'down' };
  const caracteres = [...visible];

  // Los anchos se MIDEN con la tipografia ya cargada. Estimarlos amontona unas
  // letras y separa otras, que es lo que delata al texto curvado hecho a ojo.
  const anchosMm = caracteres.map((caracter) => {
    const medida = new fabric.FabricText(caracter, {
      fontSize: adapter.toPx(layer.sizeMm),
      fontFamily: layer.font.family,
      fontWeight: layer.font.weight,
      fontStyle: layer.font.italic ? 'italic' : 'normal',
      charSpacing: layer.tracking * 100,
    } as never);
    return adapter.toMm(medida.width ?? 0);
  });

  const posiciones = disponerEnArco(caracteres, anchosMm, arco.radiusMm, arco.direction);
  const letras = posiciones.map((posicion) => {
    const objeto = new fabric.FabricText(posicion.caracter, {
      ...estilo,
      left: adapter.toPx(posicion.xMm),
      top: adapter.toPx(posicion.yMm),
      angle: posicion.gradosGiro,
      originX: 'center',
      originY: 'center',
    } as never);
    return objeto;
  });

  return new fabric.Group(letras, { ...common, subTargetCheck: false } as never);
}
